/**
 * @fileoverview Quick action types for Event Cards
 *
 * Defines the type system for contextual CTAs and secondary actions
 * on event cards, including placement hierarchy, overflow management,
 * and context-based display rules.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */

import type { Event, EventStatus } from './event';

/**
 * Action type enumeration
 * Represents all possible CTA actions on an event card
 */
export enum ActionType {
  RSVP = 'rsvp',
  JOIN_NOW = 'join_now',
  GET_TICKETS = 'get_tickets',
  JOIN_WAITLIST = 'join_waitlist',
  VIEW_RECORDING = 'view_recording',
  VIEW_DETAILS = 'view_details',
  MANAGE_RSVP = 'manage_rsvp',
  SAVE = 'save',
  SHARE = 'share',
  ADD_TO_CALENDAR = 'add_to_calendar',
}

/**
 * Event state enumeration for action context
 */
export enum EventState {
  UPCOMING = 'upcoming',
  LIVE = 'live',
  PAST = 'past',
  SOLD_OUT = 'sold_out',
  WAITLIST = 'waitlist',
  CANCELED = 'canceled',
  POSTPONED = 'postponed',
  EXTERNAL = 'external',
  FULL = 'full',
  OPEN = 'open',
}

/**
 * User role enumeration
 */
export enum UserRole {
  ANONYMOUS = 'anonymous',
  AUTHENTICATED = 'authenticated',
  REGISTERED = 'registered',
  ORGANIZER = 'organizer',
  ADMIN = 'admin',
}

/**
 * Platform enumeration
 */
export enum Platform {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

/**
 * Calendar provider enumeration
 */
export enum CalendarProvider {
  GOOGLE = 'google',
  OUTLOOK = 'outlook',
  APPLE = 'apple',
  ICS_DOWNLOAD = 'ics',
}

/**
 * Event context for determining available actions
 */
export interface EventContext {
  /** Current event state */
  state: EventState;
  /** Whether event has tickets available */
  hasTickets?: boolean;
  /** Whether event has recording available */
  hasRecording?: boolean;
  /** Whether event uses external ticketing */
  isExternal?: boolean;
  /** Whether event is within join window */
  isWithinJoinWindow?: boolean;
  /** Event start time */
  startTime: Date;
  /** Event end time */
  endTime?: Date;
}

/**
 * User context for determining available actions
 */
export interface UserContext {
  /** User authentication status */
  isLoggedIn: boolean;
  /** User role */
  role: UserRole;
  /** Whether user is registered for event */
  isRegistered?: boolean;
  /** Whether user is event organizer */
  isOrganizer?: boolean;
  /** Whether user has saved event */
  isSaved?: boolean;
}

/**
 * Display context for responsive behavior
 */
export interface DisplayContext {
  /** Current platform/viewport */
  platform: Platform;
  /** Viewport width */
  viewportWidth: number;
}

/**
 * Action availability configuration
 */
export interface ActionAvailability {
  /** Requires user to be logged in */
  loggedIn?: boolean;
  /** Available in specific event states */
  eventState?: EventState[];
  /** Available for specific user roles */
  userRole?: UserRole[];
  /** Available on specific platforms */
  platform?: Platform[];
}

/**
 * Secondary action configuration
 */
export interface SecondaryAction {
  /** Action type identifier */
  type: ActionType;
  /** Priority for display order (lower = higher priority) */
  priority: number;
  /** Availability conditions */
  availability: ActionAvailability;
  /** Placement strategy */
  placement: 'inline' | 'overflow' | 'hover';
  /** Icon identifier (Lucide icon name) */
  icon: string;
  /** Accessible label */
  label: string;
  /** Tooltip text */
  tooltip: string;
}

/**
 * Primary action configuration
 */
export interface PrimaryAction {
  /** Action type */
  type: ActionType;
  /** Display label */
  label: string;
  /** Whether action is disabled */
  disabled: boolean;
  /** Disabled reason (for tooltip) */
  disabledReason?: string;
  /** Icon identifier (Lucide icon name) */
  icon?: string;
  /** Whether action opens external link */
  isExternal?: boolean;
  /** Action URL (for links) */
  url?: string;
}

/**
 * Responsive action layout configuration
 */
export interface ActionLayout {
  /** Mobile layout (< 768px) */
  mobile: {
    primaryCTA: { width: '100%'; height: '44px' };
    secondaryActions: { maxVisible: 2; overflow: 'menu' };
    touchTarget: { minSize: '44px' };
  };
  /** Tablet layout (768px - 1024px) */
  tablet: {
    primaryCTA: { width: 'auto'; height: '40px' };
    secondaryActions: { maxVisible: 3; overflow: 'inline' };
  };
  /** Desktop layout (> 1024px) */
  desktop: {
    primaryCTA: { width: 'auto'; height: '36px' };
    secondaryActions: { maxVisible: 4; overflow: 'hover' };
  };
}

/**
 * CTA state combining primary and secondary actions
 */
export interface CTAState {
  /** Primary call-to-action */
  primary: PrimaryAction;
  /** Secondary actions list */
  secondary: SecondaryAction[];
  /** Event context */
  eventContext: EventContext;
  /** User context */
  userContext: UserContext;
}

/**
 * Action result after execution
 */
export interface ActionResult {
  /** Whether action succeeded */
  success: boolean;
  /** Result message */
  message?: string;
  /** Next suggested action */
  nextAction?: ActionType;
  /** Analytics event data */
  analytics: AnalyticsEvent;
}

/**
 * Analytics event for tracking
 */
export interface AnalyticsEvent {
  /** Event name */
  event: string;
  /** Event properties */
  properties: {
    /** Action that was taken */
    action: ActionType;
    /** Event ID */
    eventId: string;
    /** Event title */
    eventTitle: string;
    /** User context */
    userRole: UserRole;
    /** Platform */
    platform: Platform;
    /** Additional metadata */
    metadata?: Record<string, unknown>;
  };
}

/**
 * Calendar integration configuration
 */
export interface CalendarIntegration {
  /** Generate ICS file content */
  generateICS(event: Event): string;
  /** Get calendar provider deep link */
  getProviderLink(event: Event, provider: CalendarProvider): string;
  /** Detect user's preferred calendar provider */
  detectUserPreference(): CalendarProvider;
}

/**
 * Generic element type for cross-platform compatibility
 * Represents a focusable UI element (DOM Element in web, View in React Native)
 */
export type FocusableElement = unknown;

/**
 * Keyboard navigation configuration
 */
export interface KeyboardNavigation {
  /** Tab order for focusable elements */
  tabOrder: FocusableElement[];
  /** Key event handlers */
  keyHandlers: {
    Enter: () => void;
    Space: () => void;
    Escape: () => void;
    ArrowKeys: () => void;
  };
  /** Focus management utilities */
  focusManagement: FocusManager;
}

/**
 * Focus manager interface
 */
export interface FocusManager {
  /** Set focus to element */
  setFocus(element: FocusableElement): void;
  /** Get currently focused element */
  getFocused(): FocusableElement | null;
  /** Move focus to next element */
  focusNext(): void;
  /** Move focus to previous element */
  focusPrevious(): void;
  /** Trap focus within container */
  trapFocus(container: FocusableElement): void;
  /** Release focus trap */
  releaseFocusTrap(): void;
}

/**
 * Action feedback state for loading/success/error
 */
export interface ActionFeedback {
  /** Loading state */
  loading: LoadingState;
  /** Success state */
  success: SuccessState;
  /** Error state */
  error: ErrorState;
  /** Confirmation state */
  confirmation: ConfirmationState;
}

/**
 * Loading state
 */
export interface LoadingState {
  /** Whether action is loading */
  isLoading: boolean;
  /** Loading message */
  message?: string;
}

/**
 * Success state
 */
export interface SuccessState {
  /** Whether action succeeded */
  isSuccess: boolean;
  /** Success message */
  message?: string;
}

/**
 * Error state
 */
export interface ErrorState {
  /** Whether action has error */
  hasError: boolean;
  /** Error message */
  message?: string;
  /** Whether error is retryable */
  retryable?: boolean;
}

/**
 * Confirmation state
 */
export interface ConfirmationState {
  /** Whether confirmation is needed */
  needsConfirmation: boolean;
  /** Confirmation message */
  message?: string;
  /** Confirmation callback */
  onConfirm?: () => void;
  /** Cancel callback */
  onCancel?: () => void;
}
